import type { CSSProperties } from 'react';
import type { FieldValidation, Rule } from '@helios/validation';

// ─── Shared Styles ───

const vcLabelStyle: CSSProperties = {
  fontSize: 'var(--font-size-base, 14px)',
  fontWeight: 'var(--font-weight-medium, 500)',
  lineHeight: 'var(--line-height-base, 20px)',
  letterSpacing: '-0.15px',
  color: 'var(--text, #111827)',
};

const vcInputStyle: CSSProperties = {
  width: '100%',
  height: 'var(--input-height, 42px)',
  padding: '0 var(--input-padding-x, 16px)',
  borderRadius: 'var(--input-radius, 10px)',
  border: '1px solid var(--border, #e5e7eb)',
  background: 'var(--input-bg, #ffffff)',
  fontSize: 'var(--font-size-base, 14px)',
  lineHeight: 'var(--line-height-base, 20px)',
  letterSpacing: '-0.15px',
  color: 'var(--input-text, #0a0a0a)',
  fontFamily: 'inherit',
  boxSizing: 'border-box',
};

// ─── Constants ───

export const FORMAT_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'alpha', label: 'Alpha' },
  { value: 'alphanumeric', label: 'Alphanumeric' },
  { value: 'numeric', label: 'Numeric' },
  { value: 'url', label: 'URL' },
] as const;

export const DATE_PRESET_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'today', label: 'Today' },
  { value: 'now', label: 'Now' },
  { value: 'custom', label: 'Custom…' },
] as const;

// ─── Toggle button style helper ───

function toggleBtnStyle(active: boolean): CSSProperties {
  return {
    width: 44,
    height: 24,
    borderRadius: 12,
    border: 'none',
    background: active ? 'var(--color-primary, #2563eb)' : 'var(--bg-surface-2, #e5e7eb)',
    cursor: 'pointer',
    position: 'relative',
    transition: 'background 150ms',
    padding: 0,
  };
}

function toggleKnobStyle(active: boolean): CSSProperties {
  return {
    position: 'absolute',
    top: 2,
    left: active ? 22 : 2,
    width: 20,
    height: 20,
    borderRadius: 'var(--radius-full)',
    background: '#fff',
    boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
    transition: 'left 150ms',
  };
}

// ─── Component ───

export function ValidationControls({
  question,
  fieldValidation,
  onUpdate,
}: {
  question: { fieldKey: string; type: string };
  fieldValidation: FieldValidation | undefined;
  onUpdate: (fieldKey: string, fieldType: string, rules: Rule[]) => void;
}) {
  const rules = fieldValidation?.rules ?? [];

  // Helper: find a rule by kind
  const findRule = <K extends Rule['kind']>(kind: K) =>
    rules.find((r: Rule) => r.kind === kind) as Extract<Rule, { kind: K }> | undefined;

  // Helper: replace/add/remove a rule by kind
  const setRule = (kind: Rule['kind'], rule: Rule | null) => {
    const next = rules.filter((r: Rule) => r.kind !== kind);
    if (rule) next.push(rule);
    onUpdate(question.fieldKey, question.type, next);
  };

  const qType = question.type;

  // ── Text / Textarea validations ──
  if (qType === 'text' || qType === 'textarea') {
    const formatRule = findRule('string.format');
    const minRule = findRule('string.minLength');
    const maxRule = findRule('string.maxLength');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4, 16px)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm, 12px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            lineHeight: 'var(--line-height-sm, 16px)',
            color: 'var(--text, #111827)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Validation
        </span>

        {/* Format */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={vcLabelStyle}>Format</label>
          <select
            value={formatRule?.value ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setRule('string.format', null);
              } else {
                setRule('string.format', {
                  kind: 'string.format',
                  value: v as 'email' | 'phone' | 'alpha' | 'alphanumeric' | 'numeric' | 'url',
                });
              }
            }}
            style={vcInputStyle}
          >
            {FORMAT_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        {/* Min Length */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={vcLabelStyle}>Min Length</label>
          <input
            type="number"
            min={0}
            value={minRule?.value ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setRule('string.minLength', null);
              } else {
                setRule('string.minLength', { kind: 'string.minLength', value: Number(v) });
              }
            }}
            placeholder="No minimum"
            style={vcInputStyle}
          />
        </div>

        {/* Max Length */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={vcLabelStyle}>Max Length</label>
          <input
            type="number"
            min={0}
            value={maxRule?.value ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setRule('string.maxLength', null);
              } else {
                setRule('string.maxLength', { kind: 'string.maxLength', value: Number(v) });
              }
            }}
            placeholder="No maximum"
            style={vcInputStyle}
          />
        </div>
      </div>
    );
  }

  // ── Number validations ──
  if (qType === 'number') {
    const minRule = findRule('number.min');
    const maxRule = findRule('number.max');
    const intRule = findRule('number.int');

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4, 16px)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm, 12px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            lineHeight: 'var(--line-height-sm, 16px)',
            color: 'var(--text, #111827)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Validation
        </span>

        {/* Min */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={vcLabelStyle}>Min</label>
          <input
            type="number"
            value={minRule?.value ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setRule('number.min', null);
              } else {
                setRule('number.min', { kind: 'number.min', value: Number(v) });
              }
            }}
            placeholder="No minimum"
            style={vcInputStyle}
          />
        </div>

        {/* Max */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={vcLabelStyle}>Max</label>
          <input
            type="number"
            value={maxRule?.value ?? ''}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setRule('number.max', null);
              } else {
                setRule('number.max', { kind: 'number.max', value: Number(v) });
              }
            }}
            placeholder="No maximum"
            style={vcInputStyle}
          />
        </div>

        {/* Integer toggle */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <label style={vcLabelStyle}>Integer only</label>
          <button
            onClick={() => {
              if (intRule) {
                setRule('number.int', null);
              } else {
                setRule('number.int', { kind: 'number.int', value: true });
              }
            }}
            style={toggleBtnStyle(!!intRule)}
          >
            <span style={toggleKnobStyle(!!intRule)} />
          </button>
        </div>
      </div>
    );
  }

  // ── Date validations ──
  if (qType === 'date') {
    const minRule = findRule('date.min');
    const maxRule = findRule('date.max');

    const minPreset = !minRule
      ? ''
      : minRule.value === 'today' || minRule.value === 'now'
        ? minRule.value
        : 'custom';
    const maxPreset = !maxRule
      ? ''
      : maxRule.value === 'today' || maxRule.value === 'now'
        ? maxRule.value
        : 'custom';
    const preventFuture = maxRule?.value === 'today';
    const preventPast = minRule?.value === 'today';

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-4, 16px)' }}>
        <span
          style={{
            fontSize: 'var(--font-size-sm, 12px)',
            fontWeight: 'var(--font-weight-semi, 600)',
            lineHeight: 'var(--line-height-sm, 16px)',
            color: 'var(--text, #111827)',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          }}
        >
          Validation
        </span>

        {/* Min date */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={vcLabelStyle}>Min Date</label>
          <select
            value={minPreset}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setRule('date.min', null);
              } else if (v === 'custom') {
                setRule('date.min', {
                  kind: 'date.min',
                  value: new Date().toISOString().slice(0, 10),
                });
              } else {
                setRule('date.min', { kind: 'date.min', value: v });
              }
            }}
            style={vcInputStyle}
          >
            {DATE_PRESET_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {minPreset === 'custom' && (
            <input
              type="date"
              value={minRule?.value ?? ''}
              onChange={(e) => {
                setRule(
                  'date.min',
                  e.target.value ? { kind: 'date.min', value: e.target.value } : null,
                );
              }}
              style={vcInputStyle}
            />
          )}
        </div>

        {/* Max date */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-2, 8px)' }}>
          <label style={vcLabelStyle}>Max Date</label>
          <select
            value={maxPreset}
            onChange={(e) => {
              const v = e.target.value;
              if (!v) {
                setRule('date.max', null);
              } else if (v === 'custom') {
                setRule('date.max', {
                  kind: 'date.max',
                  value: new Date().toISOString().slice(0, 10),
                });
              } else {
                setRule('date.max', { kind: 'date.max', value: v });
              }
            }}
            style={vcInputStyle}
          >
            {DATE_PRESET_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          {maxPreset === 'custom' && (
            <input
              type="date"
              value={maxRule?.value ?? ''}
              onChange={(e) => {
                setRule(
                  'date.max',
                  e.target.value ? { kind: 'date.max', value: e.target.value } : null,
                );
              }}
              style={vcInputStyle}
            />
          )}
        </div>

        {/* Quick toggles */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <label style={vcLabelStyle}>Prevent future</label>
          <button
            onClick={() => {
              if (preventFuture) {
                setRule('date.max', null);
              } else {
                setRule('date.max', { kind: 'date.max', value: 'today' });
              }
            }}
            style={toggleBtnStyle(preventFuture)}
          >
            <span style={toggleKnobStyle(preventFuture)} />
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <label style={vcLabelStyle}>Prevent past</label>
          <button
            onClick={() => {
              if (preventPast) {
                setRule('date.min', null);
              } else {
                setRule('date.min', { kind: 'date.min', value: 'today' });
              }
            }}
            style={toggleBtnStyle(preventPast)}
          >
            <span style={toggleKnobStyle(preventPast)} />
          </button>
        </div>
      </div>
    );
  }

  // No validation controls for select, multi-select, yes-no, checkbox in v1
  return null;
}
